/* eslint-disable react/style-prop-object --
   The DS ui/Button exposes a variant prop literally named `style`
   (style="stroke" | ...), which this rule mistakes for the DOM style object. */
import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import { Column } from '@/types/common';
import { warehouseApi, WarehouseStock } from '@/services/api/warehouse';
import {
  codeCls,
  fmtDate,
  fmtQty,
  HubCell,
  ListCard,
  metaCls,
  nameCls,
  numCls,
  PageHead,
  StatTile,
  useListState,
  useLoad,
} from './shared';
import WarehouseFilter from './WarehouseFilter';
import { STOCK_FILTERS } from './filterFields';
import StockExportDialog from './StockExportDialog';
import StockMovementHistory from './StockMovementHistory';

/** Expiry within this many days gets a "N hari lagi" note. */
const EXPIRY_WARN_DAYS = 45;

/**
 * Where the batch sits, in one cell: the holding storage (bin, section or
 * location — stock can sit at any level) with the levels above it as a path.
 */
const StorageCell: React.FC<{ s: WarehouseStock }> = ({ s }) => {
  const bin = s.storage?.category === 'BIN' ? s.storage.name : null;
  const levels = [s.location?.name, s.section?.name, bin].filter(
    (x): x is string => !!x
  );
  const holder = levels[levels.length - 1] ?? s.storageName;
  const path = levels.slice(0, -1).join(' / ');
  if (!holder) return <span className={metaCls}>—</span>;
  return (
    <div className="max-w-[160px]" title={levels.join(' / ')}>
      <p className={`${nameCls} truncate`}>{holder}</p>
      {path && <p className={`${metaCls} truncate`}>{path}</p>}
    </div>
  );
};

const BatchExpiryCell: React.FC<{ s: WarehouseStock }> = ({ s }) => {
  const days = s.expiredDate
    ? dayjs(s.expiredDate).diff(dayjs().startOf('day'), 'day')
    : null;
  const note =
    days === null
      ? null
      : days < 0
        ? 'expired'
        : days <= EXPIRY_WARN_DAYS
          ? `${days} hari lagi`
          : null;
  return (
    <div>
      <p className="font-mono text-xs text-text-sub-600">
        {s.batchNumber || '—'}
      </p>
      <p className={metaCls}>
        {s.expiredDate ? `Exp ${fmtDate(s.expiredDate)}` : 'Tanpa expiry'}
        {note && (
          <span
            className={`ml-1 ${days !== null && days < 0 ? 'text-error-base' : 'text-warning-dark'}`}
          >
            · {note}
          </span>
        )}
      </p>
    </div>
  );
};

// Kept to seven columns so the quantities fit without scrolling at 1280px;
// the logistic web's separate Location / Section / Bin, Batch / Exp and UoM
// columns are folded into the cells next to them.
const COLUMNS: Column<WarehouseStock>[] = [
  {
    key: 'sku',
    title: 'SKU / item',
    sticky: 'left',
    render: (_v, s) => (
      <div className="max-w-[200px]">
        <p className={codeCls}>{s.sku}</p>
        <p className={`${metaCls} truncate`} title={s.itemName}>
          {s.itemName}
        </p>
      </div>
    ),
  },
  {
    key: 'hub',
    title: 'Hub Name',
    render: (_v, s) => <HubCell hub={s.hub} hubID={s.hubID} />,
  },
  {
    key: 'storage',
    title: 'Storage',
    render: (_v, s) => <StorageCell s={s} />,
  },
  {
    key: 'batch',
    title: 'Batch / Exp',
    render: (_v, s) => <BatchExpiryCell s={s} />,
  },
  {
    key: 'qtyOnHand',
    title: 'On hand',
    align: 'right',
    render: (_v, s) => <span className={numCls}>{fmtQty(s.qtyOnHand)}</span>,
  },
  {
    key: 'qtyReserved',
    title: 'Reserved',
    align: 'right',
    render: (_v, s) => (
      <span
        className={`text-sm tabular-nums ${
          s.qtyReserved ? 'text-warning-dark' : 'text-text-soft-400'
        }`}
      >
        {fmtQty(s.qtyReserved)}
      </span>
    ),
  },
  {
    key: 'qtyAvailable',
    title: 'Available',
    align: 'right',
    render: (_v, s) => (
      <div className="text-right">
        <p className="text-sm font-semibold tabular-nums text-text-strong-950">
          {fmtQty(s.qtyAvailable)}
        </p>
        <p className={metaCls}>{s.uomName}</p>
      </div>
    ),
  },
];

const StockPage: React.FC = () => {
  const q = useListState('ALL');
  const search = q.search || undefined;
  const [exportOpen, setExportOpen] = useState(false);

  // Tiles and table follow the same search, so the tiles total the table.
  const summary = useLoad(
    () => warehouseApi.getStockSummary({ search }, q.filters),
    [search, q.filterKey]
  );
  // Quarantine tile: same search, only stock held in quarantine locations.
  const quarantine = useLoad(
    () =>
      warehouseApi.getStockSummary(
        { search, locationType: 'QUARANTINE' },
        q.filters
      ),
    [search, q.filterKey]
  );
  const list = useLoad(
    () =>
      warehouseApi.listStocks(
        { search, page: q.page, limit: q.limit },
        q.filters
      ),
    [search, q.filterKey, q.page, q.limit]
  );
  const s = summary.data;
  const quarantined = quarantine.data;
  // "Stock in warehouse" = every unit; "Stock on hand" = the same minus what
  // sits in quarantine locations.
  const qtyOnHandFree =
    s && quarantined
      ? Math.max(0, s.qtyOnHand - quarantined.qtyOnHand)
      : undefined;

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <PageHead
        title="Stock"
        description="Stok barang Anda di semua hub Dash — on hand, reserved untuk outbound, dan yang masih tersedia per batch."
        actions={
          <Button
            tone="neutral"
            style="stroke"
            onClick={() => setExportOpen(true)}
            leftIcon={<i className="ri-download-2-line text-[14px]" />}
          >
            Download CSV
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
        <StatTile label="SKUs in stock" value={s ? fmtQty(s.skuCount) : '—'} />
        <StatTile
          label="Storages in use"
          value={s ? fmtQty(s.binsInUse) : '—'}
        />
        <StatTile
          label="Stock in warehouse"
          value={s ? fmtQty(s.qtyOnHand) : '—'}
          hint="Semua stok, termasuk quarantine"
        />
        <StatTile
          label="Stock on hand"
          value={qtyOnHandFree !== undefined ? fmtQty(qtyOnHandFree) : '—'}
          hint="Di luar quarantine"
        />
        <StatTile
          label="Stock reserved"
          value={s ? fmtQty(s.qtyReserved) : '—'}
        />
        <StatTile
          label="Stock available"
          value={s ? fmtQty(s.qtyAvailable) : '—'}
        />
        <StatTile
          label="Stock quarantine"
          value={quarantined ? fmtQty(quarantined.qtyOnHand) : '—'}
        />
      </div>

      <ListCard<WarehouseStock, 'ALL'>
        searchPlaceholder="Cari SKU, nama item, batch number, atau storage…"
        onSearch={q.setSearch}
        filter={
          <WarehouseFilter
            title="Stock"
            fields={STOCK_FILTERS}
            value={q.filters}
            onApply={q.setFilters}
          />
        }
        columns={COLUMNS}
        rows={list.data?.data ?? []}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        emptyTitle="Belum ada stok"
        page={q.page}
        limit={q.limit}
        total={list.data?.pagination.total ?? 0}
        onPageChange={q.setPage}
        onLimitChange={q.setLimit}
      />
      <StockMovementHistory />

      <StockExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        search={search}
        filters={q.filters}
      />
    </div>
  );
};

export default StockPage;
